import _ from "lodash/fp";

import { getLetter, isAdmin } from "./utils";

import * as self from "./item";

const addInteraction = (item) => (interaction) => {
  const { interactions = [] } = item;
  const position = self.getInteractionPosition(interactions)(interaction);
  return {
    ...item,
    interactions: Array.from(interactions).splice(position, 0, interaction),
  };
};

const checkForEmptyCorrectResponse = (interactions) => {
  return interactions
    ?.filter((o) => {
      return o.__typename === "TextEntryInteraction" && !o.correctResponse;
    })
    .map((o) => `Interaction ${o.identifier} has an empty Correct Response`);
};

const checkIfAssetIsInUse = (imageAssets) => (item) => {
  if (!Array.isArray(imageAssets) || !item) return false;

  let contents = [item.content || ""];
  if (Array.isArray(item.interactions)) {
    item.interactions.forEach((interaction) => {
      if (Array.isArray(interaction.foils)) {
        interaction.foils.forEach((foil) => {
          if (foil.content) contents.push(foil.content);
        });
      }
    });
  }

  if (item.referenceContent) {
    contents.push(item.referenceContent);
  }

  return imageAssets.some((asset) =>
    contents.some((content) => content.includes(asset.name)),
  );
};

const isInReviewState = (item) => {
  if (!item || !item.developmentState) return false;
  return (
    item.developmentState === "Content Review 4" ||
    item.developmentState === "Final Review"
  );
};

const isOpenedFromSearchAndIsAdmin = (inWorkflow) => (item) => (user) => {
  return !inWorkflow && isAdmin(user)(item?.program);
};

const checkForMissingPrintableAssets =
  (item) =>
  (objectType = "") =>
  (inWorkflow) =>
  (user) => {
    if (!item?.imageAssets || !Array.isArray(item.imageAssets)) return [];
    const isAdminSearch = isOpenedFromSearchAndIsAdmin(inWorkflow)(item)(user);
    const isReviewTask = inWorkflow && isInReviewState(item);
    if (!isAdminSearch && !isReviewTask) return [];

    const missingAssets = item.imageAssets.filter(
      (asset) => !asset.printableAsset,
    );

    if (missingAssets.length === 0) return [];

    const isAssetInUse = checkIfAssetIsInUse(item.imageAssets)(item);
    if (!isAssetInUse || !item.imageAssets) return [];

    const prefix = objectType ? `${objectType} has` : "There are";
    return [
      `${prefix} ${missingAssets.length} ${missingAssets.length === 1 ? "image" : "images"} with no printable asset`,
    ];
  };

const createInteraction =
  ({ interactions = [] }) =>
  (type) => {
    const identifier = self.generateIdentifier(type)(interactions);
    const defaultInteraction = self.getDefaultInteraction(type);
    /**
    console.debug(
      "Created interaction %s - %s - %o",
      identifier,
      type,
      defaultInteraction,
    );
*/
    return { ...defaultInteraction, identifier };
  };

const deriveKeyValueFromInteraction = (interactions = []) => {
  if (!interactions || interactions.length < 1) return "";
  return interactions
    .filter(({ __typename }) => __typename === "ChoiceInteraction")
    .flatMap(({ foils }) =>
      foils.reduce((keyAcc, foil, index) => {
        return foil.correct ? [...keyAcc, getLetter(index)] : [...keyAcc];
      }, []),
    )
    .join(",");
};

const generateIdentifier = (type) => {
  const interactionTypes = [
    {
      abbrev: "BM",
      type: "Match",
    },
    {
      abbrev: "BO",
      type: "Order",
    },
    {
      abbrev: "CR",
      type: "Extended Text",
    },
    {
      abbrev: "DTE",
      type: "Demographic Text Entry",
    },
    {
      abbrev: "GGM",
      type: "Graphic Gap Match",
    },
    {
      abbrev: "HS",
      type: "Hotspot",
    },
    {
      abbrev: "IC",
      type: "Inline Choice",
    },
    {
      abbrev: "MT",
      type: "Match Table",
    },
    {
      abbrev: "SR",
      type: "Choice",
    },
    {
      abbrev: "TE",
      type: "Text Entry",
    },
    {
      abbrev: "TGM",
      type: "Gap Match",
    },
  ];

  const findUniqueIdentifier = (abbrev) => (names) => {
    let i = 1;
    while (names.includes(`${abbrev}${i}`)) {
      i++;
    }
    return `${abbrev}${i}`;
  };

  return _.pipe([
    _.map((interaction) => interaction.identifier),
    findUniqueIdentifier(
      _.pipe([_.find({ type }), _.get("abbrev")])(interactionTypes),
    ),
  ]);
};

const getDefaultInteraction = (type) => {
  switch (type) {
    case "Choice":
      return {
        __typename: "ChoiceInteraction",
        foils: [...Array(4)].map(() => ({
          content: "<p></p>",
          correct: false,
          distractorRationale: "<p></p>",
        })),
        maxChoices: 1,
        minChoices: 0,
      };
    case "Demographic Text Entry":
      return {
        __typename: "DemographicTextEntryInteraction",
        correctResponse: "",
        expectedLength: 100,
        ignoreCase: true,
        patternMask: "",
        placeholderText: "",
      };
    case "Extended Text":
      return {
        __typename: "ExtendedTextInteraction",
        expectedLength: null,
        expectedLines: null,
        scoringCriteria: "<p></p>",
      };
    case "Gap Match":
      return {
        __typename: "GapMatchInteraction",
        elements: [
          {
            content: "",
            identifier: "Element1",
            matchGroup: null,
            matchMax: 0,
          },
        ],
        targets: [
          {
            correctResponse: null,
            identifier: "Target1",
            matchGroup: null,
            width: 100,
          },
        ],
      };
    case "Graphic Gap Match":
      return {
        __typename: "GraphicGapMatchInteraction",
        backgroundFileName: "",
        backgroundHeight: 10,
        backgroundWidth: 10,
        correctResponses: [
          {
            element: "",
            target: "",
          },
        ],
        elements: [
          {
            identifier: "Element1",
            imageUrl: "",
            matchMax: 0,
          },
        ],
        targets: [
          {
            height: 10,
            identifier: "Target1",
            matchMax: 0,
            width: 10,
            xCoord: 0,
            yCoord: 0,
          },
        ],
      };
    case "Hotspot":
      return {
        __typename: "HotspotInteraction",
        backgroundFileName: "",
        backgroundHeight: 10,
        backgroundWidth: 10,
        elements: [
          {
            colorStyle: "Blue",
            correctResponse: false,
            height: 10,
            identifier: "Element1",
            left: 10,
            radius: 10,
            shape: "",
            top: 10,
            width: 10,
            xCoord: 10,
            yCoord: 10,
          },
        ],
        maxChoices: 0,
      };
    case "Inline Choice":
      return {
        __typename: "InlineChoiceInteraction",
        choices: [...Array(4)].map(() => ({
          correct: false,
          text: "<p></p>",
        })),
        distractorRationale: "<p></p>",
      };
    case "Match":
      return {
        __typename: "MatchInteraction",
        correctResponse: [
          {
            element: null,
            target: null,
          },
        ],
        elements: [
          {
            content: "<p></p>",
            identifier: "Element1",
            matchGroup: null,
            matchMax: 0,
          },
        ],
        maxElementsPerRow: 0,
        targets: [
          {
            identifier: "Target1",
            matchGroup: null,
            matchMax: 0,
            text: "",
            width: 100,
          },
        ],
      };
    case "Match Table":
      return {
        __typename: "MatchTableInteraction",
        columnChoices: [],
        rowChoices: [],
        class: "",
        maxAssociations: 1,
        minAssociations: 1,
        prompt: "",
        identifier: "",
        rowCentric: true,
        shuffle: "",
        templateName: "defaultTemplate",
      };
    case "Order":
      return {
        __typename: "OrderInteraction",
        correctResponse: [
          {
            identifier: "Element1",
          },
          {
            identifier: "Element2",
          },
        ],
        elements: [...Array(2)].map((_value, index) => ({
          content: "<p></p>",
          identifier: `Element${index + 1}`,
        })),
        orientation: "vertical",
      };
    case "Text Entry":
      return {
        __typename: "TextEntryInteraction",
        correctResponse: "",
        expectedLength: 100,
        ignoreCase: true,
        patternMask: "",
        placeholderText: "",
      };
    default:
      return {};
  }
};

const getInteractionPosition =
  (interactions = []) =>
  (interaction) => {
    const sortedInteractions = [...interactions, interaction]
      .filter(({ identifier }) => !!identifier)
      .sort((a, b) => a.identifier.localeCompare(b.identifier));
    return sortedInteractions.findIndex(
      (o) => o.identifier === interaction.identifier,
    );
  };

const getSaveWarnings = (item) => (passage) => (inWorkflow) => (user) => {
  return [
    self.checkForEmptyCorrectResponse(item?.interactions),
    checkForMissingPrintableAssets(item)("Item")(inWorkflow)(user),
    checkForMissingPrintableAssets(passage)("Passage")(inWorkflow)(user),
  ]
    .flat()
    .filter(Boolean);
};

const hasUndefinedFoilContent = (interactions) => {
  return (interactions || []).some((interaction) =>
    (interaction?.foils || []).some((foil) => {
      const content = foil?.content?.replace(/<[^>]*>/g, "").trim();
      const rationale = foil?.distractorRationale
        ?.replace(/<[^>]*>/g, "")
        .trim();
      return content === "undefined" || rationale === "undefined";
    }),
  );
};

export {
  addInteraction,
  checkForEmptyCorrectResponse,
  checkForMissingPrintableAssets,
  createInteraction,
  deriveKeyValueFromInteraction,
  generateIdentifier,
  getDefaultInteraction,
  getInteractionPosition,
  getSaveWarnings,
  hasUndefinedFoilContent,
};
